import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InputRow } from "./InputRow";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import type { IDDItem } from "../../components/common/DropdownMenu";
import { DownloadButton } from "../../components/common/DownloadButton";
import { CalculateButton } from "../../components/common/CalculateButton";
import { useCalculatorMutation } from "../../store/slices/apiSlice";
import type { IDisplayField, IField, unitId } from "../../interfaces/interfaces";
import { unitConverter } from "../../components/MeasurementUnits/UnitConverter";
import { setInputUnit, setInputValue, validateInputs } from "../../store/slices/pressureDropCalculatorSlice";
import { OuterData } from "../../components/common/OuterData";
import { getDataForPressureDrop } from "../../store/slices/pvtCalculatorSlice";

const EMPTY_INPUT_IDS: string[] = [];
const validated = (inputs: IField[] | IDisplayField[]) => {
	return !inputs.some(input => input.error != null);
};

const InputsSection: React.FC = () => {
	const inputs = useAppSelector(state => state.pressureDropCalculator.inputs);
	const pvtData = useAppSelector(getDataForPressureDrop);
	const inputIds = useMemo(() => {
		if (!inputs) {
			return EMPTY_INPUT_IDS;
		}

		return inputs.map(input => input.id);
	}, [inputs]);

	const dropDownItems: { [key: string]: IDDItem[] } = useMemo(() => {
		return inputs.reduce((acc, input) => {
			const onlyUnits = input.unitList ?? [];
			const units = unitConverter.getUnitsByCategory(input.unitCategoryId).filter(unit => onlyUnits.length === 0 || onlyUnits.includes(unit.id));
			acc[input.id] = units.map(unit => ({
				id: unit.id,
				translationKey: `common:measurement_units.${unit.category}.${unit.id}`
			} as IDDItem));

			return acc;
		}, {} as { [key: string]: IDDItem[] });
	}, [inputs]);

	const { t } = useTranslation();

	const dispatch = useAppDispatch();
	const handleInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(setInputValue({ inputName, value }));
		},
		[dispatch]);

	const handleSelectUnit = useCallback((inputId: string, selectedUnitId: string | null) => {
		dispatch(setInputUnit({ inputId, toUnitId: selectedUnitId as unitId }));

	}, [dispatch, inputs]);

	const [triggerCalculator, { isLoading }] = useCalculatorMutation();

	const handleCalculate = useCallback(async () => {
		dispatch(validateInputs());

		const formData = getFormData();
		if (!formData) {
			return;
		}

		triggerCalculator({
			calculator_type: "pressure_drop",
			...formData,
		});
	}, [inputs, triggerCalculator]);

	const getFormData = useCallback(() => {
		if (!inputs || !validated(inputs)) {
			return;
		}

		const pressureDropInputs = [...inputs, ...pvtData.inputs];
		if (!validated(pressureDropInputs) || pvtData.errors.length > 0) {
			return;
		}
		const formData: Record<string, number | string> = {};
		pressureDropInputs.forEach(input => {
			formData[input.queryName] = input.baseValue ?? 0;
		});

		return formData;
	}, [inputs, pvtData]);

	return (
		<div className="pressure-drop__inputs-section">
			<fieldset className="pressure-drop__inputs round-corner-container">
				<legend>{t('main_parameters')}</legend>
				{inputIds.map((inputId) => (
					<InputRow
						key={inputId}
						inputId={inputId}
						dropdownItems={dropDownItems[inputId]}
						onInputChange={handleInputChange}
						onSelectUnit={handleSelectUnit}
					/>
				))}
			</fieldset>
			<div className="pressure-drop__buttons">
				<CalculateButton onClick={handleCalculate} disabled={isLoading} />
				<DownloadButton getFormData={getFormData} calculatorType="pressure_drop" disabled={false} title={t(`download`)} />
			</div>
			<OuterData
				inputs={pvtData.inputs}
				errors={pvtData.errors}
				translationNamespace="pvt"
			/>
		</div>
	);
};

export default memo(InputsSection);
